/**
 * Utility functions for RSVP cutoff functionality
 */

/**
 * Check if RSVP cutoff has passed
 * @param {string} rsvpCutoff - ISO datetime string
 * @returns {boolean} - True if cutoff has passed
 */
export const isRsvpCutoffPassed = (rsvpCutoff) => {
  if (!rsvpCutoff) return false;
  
  try {
    const cutoffTime = new Date(rsvpCutoff);
    const currentTime = new Date();
    return currentTime > cutoffTime;
  } catch (error) {
    console.error('Error parsing RSVP cutoff date:', error);
    return false;
  }
};

/**
 * Format RSVP cutoff date for display
 * @param {string} rsvpCutoff - ISO datetime string
 * @returns {string} - Formatted date string
 */
export const formatRsvpCutoff = (rsvpCutoff) => {
  if (!rsvpCutoff) return '';
  
  try {
    const cutoffTime = new Date(rsvpCutoff);
    return cutoffTime.toLocaleString();
  } catch (error) {
    console.error('Error formatting RSVP cutoff date:', error);
    return rsvpCutoff;
  }
};

/**
 * Validate RSVP cutoff date
 * @param {string} rsvpCutoff - ISO datetime string
 * @param {string} eventDate - Event date in YYYY-MM-DD format
 * @returns {object} - Validation result with isValid and error message
 */
export const validateRsvpCutoff = (rsvpCutoff, eventDate) => {
  if (!rsvpCutoff) {
    return { isValid: true };
  }
  
  try {
    const cutoffTime = new Date(rsvpCutoff);
    const eventDateTime = new Date(eventDate);
    
    if (cutoffTime > eventDateTime) {
      return {
        isValid: false,
        error: 'RSVP cutoff cannot be after the event date'
      };
    }
    
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      error: 'Invalid RSVP cutoff date format'
    };
  }
};

/**
 * Get time remaining until RSVP cutoff
 * @param {string} rsvpCutoff - ISO datetime string
 * @returns {object} - Time remaining object with days, hours, minutes
 */
export const getTimeUntilCutoff = (rsvpCutoff) => {
  if (!rsvpCutoff) return null;
  
  try {
    const cutoffTime = new Date(rsvpCutoff);
    const currentTime = new Date();
    const timeDiff = cutoffTime - currentTime;
    
    if (timeDiff <= 0) {
      return { expired: true };
    }
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes, expired: false };
  } catch (error) {
    console.error('Error calculating time until cutoff:', error);
    return null;
  }
};